import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import type { ImagePrompt, ServiceError, SourceFallbackImage } from '@/app/types';
import { TEMP_IMAGE_URI_PREFIX } from '../temp-image-uri';
import { uploadFile } from './upload-file';

/** 内容审核不通过 —— 这类失败不允许降级重试，否则等于绕过审核 */
const AUDIT_ILLEGAL_REASON = 'AuditSubmitIllegal';

/**
 * 是否内容审核不通过。这类失败禁止降级重试 —— 压缩整图和原图是同一张图，
 * 换一份 blob 再传一次等于绕过审核。
 */
export const isAuditIllegal = (error?: ServiceError): boolean => error?.reason === AUDIT_ILLEGAL_REASON;

/**
 * 临时图片数据
 */
export interface TempImageData {
  id: string;
  file: File;
  selectedImageBlob?: Blob;
  imageResolution: { width: number; height: number };
  selectedImageResolution?: { width: number; height: number };
  scene: UploadScene;
  /** 原图上传失败时的降级兜底图（未裁剪整图降分辨率） */
  sourceFallback?: SourceFallbackImage;
}

/** 单张临时图片的补传结果 */
export type TempImageUploadOutcome =
  | {
      ok: true;
      sourceUri: string;
      selectedUri?: string;
      /** 原图上传失败、已降级为压缩整图 */
      sourceDegraded: boolean;
      /** 原图和压缩整图均失败，源图复用裁剪图 */
      sourceFromSelected?: boolean;
    }
  | {
      ok: false;
      /** 失败发生在原图还是裁剪图 */
      stage: 'source' | 'selected';
      error?: ServiceError;
    };

/**
 * 从 ImagePrompt 中提取临时图片数据
 */
export const extractTempImageData = (prompt: ImagePrompt): TempImageData | null => {
  if (!prompt._tempFile) {
    return null;
  }

  const selectedImageBlob = prompt._tempSelectedImage ?? prompt._tempSourceFallback?.blob;
  const selectedImageResolution = prompt._tempSelectedImage
    ? prompt.selectedImageWidth && prompt.selectedImageHeight
      ? {
          width: prompt.selectedImageWidth,
          height: prompt.selectedImageHeight,
        }
      : undefined
    : prompt._tempSourceFallback
      ? {
          width: prompt._tempSourceFallback.width,
          height: prompt._tempSourceFallback.height,
        }
      : undefined;

  return {
    id: prompt.sourceImage.replace(TEMP_IMAGE_URI_PREFIX, ''),
    file: prompt._tempFile,
    selectedImageBlob,
    imageResolution: {
      width: prompt.sourceImageWidth || 0,
      height: prompt.sourceImageHeight || 0,
    },
    selectedImageResolution,
    scene: UPLOAD_SCENE.VIDU, // 默认场景，可以根据需要调整
    sourceFallback: prompt._tempSourceFallback,
  };
};

/**
 * 上传单张临时图片：先 selected 图，再原图。
 *
 * 原图失败时降级用「未裁剪的压缩整图」重试一次 —— 原图只用于同款时切清晰度/重新裁剪，
 * 丢了不影响本次生成，所以宁可降级也不要卡住用户。但审核不通过不降级（同一张图重试等于绕过审核）。
 * selected 图失败直接算失败，不再上传原图；下次点击创作时从 selected 图重新开始。
 * 原图及压缩整图都失败时，复用已经成功的 selected 图，不阻塞本次创作。
 */
const uploadSingleTempImage = async (tempImage: TempImageData): Promise<TempImageUploadOutcome> => {
  const { file, selectedImageBlob, imageResolution, selectedImageResolution, scene, sourceFallback } = tempImage;

  // 1. 先上传 selected 图片。它是模型真正消费的素材，失败时不能拿原图替代。
  let selectedUri: string | undefined;
  if (selectedImageBlob && selectedImageResolution) {
    const selectedResult = await uploadFile({
      file: selectedImageBlob,
      width: selectedImageResolution.width,
      height: selectedImageResolution.height,
      scene,
    });

    if (selectedResult.error || !selectedResult.uri) {
      console.error('Failed to upload temp selected image:', selectedResult.error);
      return { ok: false, stage: 'selected', error: selectedResult.error };
    }
    selectedUri = selectedResult.uri;
  }

  // 2. 再上传源图片；原图只用于同款清晰度/重新裁剪，失败不阻塞生成。
  let sourceResult = await uploadFile({
    file,
    width: imageResolution.width,
    height: imageResolution.height,
    scene,
  });
  let sourceDegraded = false;

  if ((sourceResult.error || !sourceResult.uri) && sourceFallback && !isAuditIllegal(sourceResult.error)) {
    console.warn('Temp source image upload failed, retrying with resized full image:', sourceResult.error);
    const fallbackResult = await uploadFile({
      file: sourceFallback.blob,
      width: sourceFallback.width,
      height: sourceFallback.height,
      scene,
    });
    if (!fallbackResult.error && fallbackResult.uri) {
      sourceResult = fallbackResult;
      sourceDegraded = true;
    }
  }

  if (sourceResult.error || !sourceResult.uri) {
    if (selectedUri) {
      return {
        ok: true,
        sourceUri: selectedUri,
        selectedUri,
        sourceDegraded: false,
        sourceFromSelected: true,
      };
    }
    console.error('Failed to upload temp image:', sourceResult.error);
    return { ok: false, stage: 'source', error: sourceResult.error };
  }

  return { ok: true, sourceUri: sourceResult.uri, selectedUri, sourceDegraded };
};

/**
 * 上传临时图片
 */
export const uploadTempImages = async (
  tempImages: TempImageData[],
  onProgress?: (uploaded: number, total: number) => void,
): Promise<Map<string, TempImageUploadOutcome>> => {
  const results = new Map<string, TempImageUploadOutcome>();

  for (let i = 0; i < tempImages.length; i++) {
    const tempImage = tempImages[i];
    results.set(tempImage.id, await uploadSingleTempImage(tempImage));
    onProgress?.(i + 1, tempImages.length);
  }

  return results;
};
